/**
 * @description
 * Root application component for the Byte Admin Dashboard.
 * Sets up routing, authentication, and provides the main application structure.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date January 6, 2025
 * @see admin/src/frontend/main.tsx
 */

import React from 'react';
import { Routes, Route, Navigate } from 'react-router';
import { Toaster } from 'sonner';
import { Toaster as HotToaster } from 'react-hot-toast';
import { AuthProvider } from './contexts/AuthContext';
import { LoginPage } from './components/auth/LoginPage';
import { AuthSuccess } from './components/auth/AuthSuccess';
import { AdminRoute } from './components/auth/AdminRoute';
import { ClientPortalRoute } from './components/auth/ClientPortalRoute';
import { Layout } from './components/Layout';
import { ClientPortalLayout } from './components/ClientPortalLayout';
import { Dashboard } from './pages/Dashboard';
import { ClientList } from './pages/ClientList';
import { ClientDetail } from './pages/ClientDetail';
import UserManagement from './pages/UserManagement';
import { Deployments } from './pages/Deployments';
import DeploymentDetails from './pages/DeploymentDetails';
import { Settings } from './pages/Settings';
import { VSCodeLens } from './pages/VSCodeLens';
import { Anthropic } from './pages/Anthropic';
import { InvoiceManagement } from './pages/InvoiceManagement';
import { MarketplaceConfig } from './pages/MarketplaceConfig/MarketplaceConfig';
import { ClientPortal } from './pages/ClientPortal';
import { ClientVSCodeLens } from './pages/ClientVSCodeLens';
import { initSentry } from './lib/sentry';
import SentryErrorBoundary from './components/error/SentryErrorBoundary';

/**
 * Main application component with routing and authentication
 */
function App() {
  // Initialize Sentry on app start
  React.useEffect(() => {
    initSentry();
  }, []);

  return (
    <SentryErrorBoundary>
      <AuthProvider>
        <div className="min-h-screen bg-background">
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/auth/success" element={<AuthSuccess />} />
            
            {/* Protected admin routes with layout */}
            <Route
              element={
                <AdminRoute>
                  <Layout />
                </AdminRoute>
              }
            >
              <Route path="/" element={<Dashboard />} />
              <Route path="/clients" element={<ClientList />} />
              <Route path="/clients/:id" element={<ClientDetail />} />
              <Route path="/users" element={<UserManagement />} />
              <Route path="/deployments" element={<Deployments />} />
              <Route path="/deployment-details/:id" element={<DeploymentDetails />} />
              <Route path="/vscode-lens" element={<VSCodeLens />} />
              <Route path="/anthropic" element={<Anthropic />} />
              <Route path="/invoices" element={<InvoiceManagement />} />
              <Route path="/marketplace-config" element={<MarketplaceConfig />} />
              <Route path="/settings" element={<Settings />} />
            </Route>
            
            {/* Client portal routes */}
            <Route
              element={
                <ClientPortalRoute>
                  <ClientPortalLayout />
                </ClientPortalRoute>
              }
            >
              <Route path="/portal/vscode-lens" element={<ClientVSCodeLens />} />
              <Route path="/portal" element={<ClientPortal />} />
            </Route>
            
            {/* 404 - Redirect to home */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster richColors position="top-right" />
          <HotToaster position="top-right" />
        </div>
      </AuthProvider>
    </SentryErrorBoundary>
  );
}

export default App;

/*
 * © 2025 Byte Inc. All rights reserved.
 * This code is the confidential and proprietary information of Byte Inc.
 * You shall not disclose such Confidential Information and shall use it only in
 * accordance with the terms of the license agreement you entered into with Byte Inc.
 */